import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, SignInStatus } from 'src/app/services/auth.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import { LoginCallbackService } from 'src/app/services/login-callback.service';
import { MultiFactorResolver } from 'firebase/auth';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  @ViewChild('totpQRCanvas') totpQRCanvas?: ElementRef<HTMLDivElement>;

  username = '';
  password = '';

  loggingIn = false;

  sendingEmailVerificationEmail = false;

  passwordResetEmail = '';
  sendingPasswordResetEmail = false;
  passwordResetEmailSent = false;

  pageState:
    | 'login'
    | '2fa-login'
    | '2fa-enroll'
    | 'verify-email'
    | 'forgot-password' = 'login';

  error = '';

  // showLogout = false;

  mfaResolver?: MultiFactorResolver;

  showEmailVerificationMessage = false;
  showEmailVerificationError = false;

  successMessage = '';

  constructor(
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private loginCallbackService: LoginCallbackService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // on page load, check if the user is already signed in
    this.authService.authReady.subscribe((authReady) => {
      console.log('login.component -> authReady: ', authReady);

      // check if we need to show email verification message or capture login
      // callback context
      this.route.queryParams.subscribe((params) => {
        try {
          // capture Electron login callback context from query params
          this.loginCallbackService.setFromQueryParams(params);
        } catch (err) {
          console.error(
            'login.component -> error setting login callback context: ',
            err
          );
          this.error = 'Invalid login context provided.';
          return;
        }

        if (params['emailVerified'] !== undefined) {
          if (params['emailVerified'] === false) {
            this.authService.isInSignUpFlow = true;
            this.pageState = 'verify-email';
            return;
          }
        }

        if (authReady && this.firebaseService.signedIn) {
          console.log('login.component -> User is signed in to firebase');
          if (!this.authService.emailVerified) {
            // user needs to complete email verification
            this.showVerifyEmail();
          } else if (!this.authService.has2FAEnrollment) {
            // user has not enrolled in 2FA
            this.show2FAEnroll();
          } else {
            if (this.authService.signedIn) {
              this.authService.isInSignUpFlow = false;
              if (this.loginCallbackService.hasElectronContext()) {
                this.redirectToElectronCallback();
                return;
              }
              this.goToPostLoginUrl();
            } else {
              // something went wrong setting initial login state, log user out
              this.authService.signOut().subscribe();
            }
          }
        }
      });
    });
  }

  get currentUser() {
    return this.authService.currentUser;
  }

  get firebaseUser() {
    return this.firebaseService.currentUser;
  }

  get signedIn() {
    return this.authService.signedIn;
  }

  get enrolledOptions() {
    return this.authService._2FAEnrollments?.map((e) => e.factorId);
  }

  showVerifyEmail() {
    this.authService.isInSignUpFlow = true;
    this.pageState = 'verify-email';
  }

  show2FAEnroll() {
    this.authService.isInSignUpFlow = true;
    this.pageState = '2fa-enroll';
  }

  show2FALogin() {
    this.authService.isInSignUpFlow = true;
    this.pageState = '2fa-login';
  }

  signInWithEmail() {
    this.loggingIn = true;
    this.authService.signInWithEmail(this.username, this.password).subscribe({
      next: this.handleSignInResponse.bind(this),
      error: this.handleSignInError.bind(this),
    });
  }

  signInWithGoogle() {
    this.loggingIn = true;
    this.authService.signInWithGoogle().subscribe({
      next: this.handleSignInResponse.bind(this),
      error: this.handleSignInError.bind(this),
    });
  }

  handleSignInResponse(res: SignInStatus) {
    // this should not succeed if the user is not signed in
    console.log('login.component -> handleSignInResponse: ', res);

    if (res.success) {
      // we should not get here if the user has 2fa enabled, something went wrong
      console.error(
        new Error(
          'User is signed in without 2fa and error condition was not met'
        )
      );
      this.error = 'Unexpected error occurred. Please try again.';
      this.loggingIn = false;
      return;
    }

    if (res.error === 'email_not_verified') {
      this.showVerifyEmail();
    } else if (res.error === 'missing_2fa') {
      this.show2FAEnroll();
    } else if (res.error === 'user_not_found') {
      this.error = 'Unable to find account with the provided email.';
    } else {
      this.error = 'Unexpected error occurred. Please try again.';
    }
    this.loggingIn = false;
  }

  handleSignInError(err: any) {
    console.error('expected error during sign in: ', err);

    if (err.code === 'auth/multi-factor-auth-required') {
      // THIS IS EXPECTED
      // this means the user has 2FA enabled
      this.error = '';
      this.authService.isInSignUpFlow = true;

      const mfaResolver = this.firebaseService.mfaResolver(err);
      this.mfaResolver = mfaResolver;
      console.log('mfaResolver: ', mfaResolver);
      console.log('login.component -> this.signedIn: ', this.signedIn);

      if (
        mfaResolver.hints.some((h) => h.factorId === 'phone') ||
        mfaResolver.hints.some((h) => h.factorId === 'totp')
      ) {
        this.show2FALogin();
        console.log(
          'login.component -> firebaseService.currentUser: ',
          this.firebaseService.currentUser
        );
      } else {
        console.error(new Error('Unsupported 2FA method'));
        this.error = 'Unsupported 2FA method.';
      }
    } else if (
      err.code === 'auth/invalid-credential' ||
      err.code === 'auth/wrong-password'
    ) {
      this.error = 'Invalid email or password';
      this.authService.isInSignUpFlow = false;
      // this.error = JSON.stringify(err, null, 2);
    } else {
      this.error = 'Unexpected error occurred. Please try again.';
    }

    this.loggingIn = false;
  }

  sendEmailVerification() {
    this.error = '';

    if (!this.firebaseService.currentUser) {
      console.error(
        new Error('Unable to send verification email. Please sign in.')
      );
      this.error = 'Unable to send verification email. Please sign in.';
      return;
    }
    const user = this.firebaseService.currentUser;
    if (user.emailVerified) {
      console.log('user email already verified', user);
      this.error = 'User email already verified';
      return;
    }
    if (!user.email) {
      console.error(
        new Error('Unable to send verification email. No email found.')
      );
      this.error = 'Unable to send verification email. No email found.';
      return;
    }
    this.sendingEmailVerificationEmail = true;
    this.authService.sendEmailVerificationEmail(user.email).subscribe({
      next: () => {
        this.sendingEmailVerificationEmail = false;
        console.log('verification email sent');
        this.successMessage = 'Verification email sent';
        this.error = '';
      },
      error: (err) => {
        this.sendingEmailVerificationEmail = false;
        console.error(
          'login.component -> sendEmailVerificationEmail error: ',
          new Error(err)
        );
        this.error = 'Error resending verification email.';
      },
    });
  }

  sendPasswordResetEmail(ev: Event) {
    ev.preventDefault();

    this.error = '';
    this.sendingPasswordResetEmail = true;

    const captchaVerifier =
      this.firebaseService.recaptchaVerifier('captcha-container');

    captchaVerifier
      .verify()
      .then(() => {
        this.authService
          .sendPasswordResetEmail(this.passwordResetEmail)
          .subscribe({
            next: () => {
              this.sendingPasswordResetEmail = false;
              console.log('password reset email sent');
              this.error = '';
              this.passwordResetEmailSent = true;
            },
            error: (err) => {
              this.sendingPasswordResetEmail = false;
              console.error(
                'login.component -> sendPasswordResetEmail error: ',
                err
              );
              if (err.error?.error === 'no account found for email') {
                this.error = 'Unable to find account with the provided email.';
              } else {
                this.error = 'Error resending password reset email.';
              }
            },
          });
      })
      .catch((err) => {
        this.sendingPasswordResetEmail = false;
        console.error(
          'login.component -> sendPasswordResetEmail captcha error: ',
          err
        );
        this.error =
          'Error verifying captcha. Please refresh the page and try again.';
      });
  }

  signOut() {
    this.error = '';
    this.authService.isInSignUpFlow = false;

    this.authService.signOut().subscribe({
      next: (res) => {
        console.log(
          `login.component -> signOut response: ${JSON.stringify(res, null, 2)}`
        );
        this.resetViews();
      },
      error: (err) => {
        console.error('login.component -> signOut error: ', err);
        this.error = 'Unexpected error occurred. Please try again.';
      },
    });
  }

  resetViews() {
    this.pageState = 'login';
    // this.showEmailVerificationMessage = false;
    this.showEmailVerificationError = false;
    this.error = '';
    this.authService.isInSignUpFlow = true;
    this.loggingIn = false;
  }

  mfaSuccess() {
    this.authService.getCurrentUserInfo().subscribe({
      next: (res: SignInStatus) => {
        console.log('login.component -> mfaSuccess: ', res);
        if (!res.success) {
          this.error = 'Unexpected error occurred. Please try again.';
          return;
        }
        this.loginSuccess();
      },
      error: (err) => {
        console.error('login.component -> mfaSuccess error: ', err);
        this.error = 'Unexpected error occurred. Please try again.';
      },
    });
  }

  loginSuccess() {
    console.log('login.component -> loginSuccess');
    this.authService.isInSignUpFlow = false;

    if (this.loginCallbackService.hasElectronContext()) {
      console.log('login.component -> redirecting to Electron callback');
      this.redirectToElectronCallback();
      return;
    }

    this.goToPostLoginUrl();
  }

  private postLoginNavigationStarted = false;

  private goToPostLoginUrl(): void {
    if (this.postLoginNavigationStarted) {
      return;
    }
    this.postLoginNavigationStarted = true;
    const redirectAfterLoginUrl = localStorage.getItem('redirectAfterLoginUrl');
    let navigation: Promise<boolean>;
    if (redirectAfterLoginUrl) {
      console.log('login.component -> redirecting to: ', redirectAfterLoginUrl);
      localStorage.removeItem('redirectAfterLoginUrl');
      navigation = this.router.navigateByUrl(redirectAfterLoginUrl);
    } else {
      console.log('login.component -> redirecting to home');
      navigation = this.router.navigate(['/']);
    }
    navigation
      .then((navigated) => {
        if (!navigated) {
          this.postLoginNavigationStarted = false;
        }
      })
      .catch((err) => {
        console.error('login.component -> post-login navigation error: ', err);
        this.postLoginNavigationStarted = false;
      });
  }

  private redirectToElectronCallback(): void {
    console.log('login.component -> redirectToElectronCallback');
    const ctx = this.loginCallbackService.getContext();
    console.log('login.component -> ctx: ', ctx);
    if (!ctx) {
      console.error('No context found for Electron callback');
      return;
    }

    this.firebaseService.getCredentialsForLoginCallback().subscribe({
      next: (creds) => {
        const params = new URLSearchParams({
          token: creds.idToken,
          refreshToken: creds.refreshToken,
          uid: creds.uid,
          email: creds.email,
        });
        let callbackUrl = '';
        if (ctx.redirect_uri) {
          const redirectUrl = new URL(ctx.redirect_uri);
          params.forEach((value, key) => {
            redirectUrl.searchParams.set(key, value);
          });
          callbackUrl = redirectUrl.toString();
        } else {
          const scheme = ctx.scheme.replace(/:\/*$/, '');
          callbackUrl = `${scheme}://auth/callback?${params.toString()}`;
        }
        console.log('login.component -> callbackUrl: ', callbackUrl);
        this.loginCallbackService.clear();
        this.invokeLocalAppCallback(callbackUrl);
        this.router.navigate(['/login-success']);
      },
      error: (err) => {
        console.error('Failed to get credentials for Electron callback', err);
        this.loginCallbackService.clear();
        this.router.navigate(['/']);
      },
    });
  }

  /**
   * Delivers credentials to the local desktop app without navigating the
   * browser away from the current page (no window.location change).
   */
  private invokeLocalAppCallback(callbackUrl: string): void {
    if (/^https?:\/\//i.test(callbackUrl)) {
      // if callback URL is a HTTP URL, use fetch to notify the local app
      fetch(callbackUrl, { mode: 'no-cors', keepalive: true }).catch((err) => {
        console.error('Failed to notify local app via fetch', err);
      });
      return;
    }

    // if callback URL is a custom protocol, use a hidden iframe to trigger
    // the callback without changing the top-level href.
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = callbackUrl;
    document.body.appendChild(iframe);
    window.setTimeout(() => iframe.remove(), 2000);
  }
}
